import { useState } from 'react';

import {
  LayoutCard,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from '@cast/design-system';
import {
  getResourceUnit,
  useClusterDashboardResourceUtilizationData,
  useClusterDashboardState,
  type ResourceType,
} from '@cast/features-cluster-shared';
import { clusterColors } from '@cast/features-cluster-ui';

import {
  ChartSummaryMetric,
  ChartSummaryMetricsRow,
} from './shared/ChartSummaryMetricsRow';
import { ClusterDashboardChart } from './shared/ClusterDashboardChart';
import { ClusterDashboardRangePicker } from './shared/ClusterDashboardRangePicker';
import { DashboardChartFrame } from './shared/DashboardChartFrame';
import { DashboardSectionError } from './shared/DashboardSectionError';
import {
  ChartSummaryMetricSkeleton,
  ChartSummaryMetricsRowSkeleton,
} from './shared/DashboardMetricsSkeleton';

const RESOURCE_SERIES = [
  {
    dataKey: 'requested',
    color: clusterColors.requested,
    displayName: 'Requested',
    type: 'area' as const,
  },
  {
    dataKey: 'used',
    color: clusterColors.used,
    displayName: 'Used',
    type: 'area' as const,
  },
  {
    dataKey: 'provisioned',
    color: clusterColors.provisioned,
    displayName: 'Provisioned',
    type: 'line' as const,
  },
  {
    dataKey: 'utilization',
    color: clusterColors.utilization,
    displayName: 'Utilization',
    type: 'line' as const,
    strokeDasharray: '4 4',
    yAxisId: 'utilization',
  },
];

export const ResourceUtilizationSection = () => {
  const { showStorage: showStorageByProvider } = useClusterDashboardState();
  const [resourceType, setResourceType] = useState<ResourceType>('cpu');
  const {
    chartData,
    averages,
    isLoading,
    hasError,
    showGpu,
    showStorage: showStorageFromCapabilities,
    refetch,
  } = useClusterDashboardResourceUtilizationData(resourceType);

  const showStorage = showStorageByProvider && showStorageFromCapabilities;
  const unit = getResourceUnit(resourceType).toUpperCase();

  return (
    <LayoutCard
      title="Resource utilization"
      headerEndContent={
        <Stack direction="row" alignItems="center" gap={8} flexWrap="wrap">
          <ClusterDashboardRangePicker />
          <ToggleButtonGroup
            size="sm"
            value={resourceType}
            onChange={(_, value) => value && setResourceType(value)}
            testId="resource-utilization-type-toggle"
          >
            <ToggleButton value="cpu" text="CPU" />
            <ToggleButton value="memory" text="Memory" />
            {showStorage ? (
              <ToggleButton value="storage" text="Storage" />
            ) : null}
            {showGpu ? (
              <ToggleButton value="gpu" text="GPU" testId="resource-type-gpu" />
            ) : null}
          </ToggleButtonGroup>
        </Stack>
      }
      testId="resource-utilization-section"
    >
      <DashboardSectionError hasError={hasError} onRetry={refetch}>
        <Stack gap={16}>
          {isLoading ? (
            <ChartSummaryMetricsRowSkeleton>
              <ChartSummaryMetricSkeleton />
              <ChartSummaryMetricSkeleton />
              <ChartSummaryMetricSkeleton />
              <ChartSummaryMetricSkeleton />
            </ChartSummaryMetricsRowSkeleton>
          ) : (
            <ChartSummaryMetricsRow>
              <ChartSummaryMetric
                color={clusterColors.provisioned}
                label="Avg. provisioned"
                unit={unit}
                value={averages.provisioned}
              />
              <ChartSummaryMetric
                color={clusterColors.used}
                label="Avg. used"
                unit={unit}
                value={averages.used}
              />
              <ChartSummaryMetric
                color={clusterColors.requested}
                label="Avg. requested"
                unit={unit}
                value={averages.requested}
              />
              <ChartSummaryMetric
                color={clusterColors.utilization}
                label="Utilization"
                unit="%"
                value={averages.utilization}
              />
            </ChartSummaryMetricsRow>
          )}

          <DashboardChartFrame>
            <ClusterDashboardChart
              data={chartData}
              isLoading={isLoading}
              hasNoData={!isLoading && !chartData.length}
              noDataOverlayProps={{ title: 'No utilization data available' }}
              series={RESOURCE_SERIES}
              showUtilizationAxis
              testId="resource-utilization-chart"
            />
          </DashboardChartFrame>
        </Stack>
      </DashboardSectionError>
    </LayoutCard>
  );
};
